/**
 * Leave API Service
 *
 * API calls for the HRMS Leave Management module.
 */

import { apiClient } from '@/lib/api-client';
import type {
  LeaveType,
  LeaveBalance,
  LeaveRequest,
  LeaveAttachment,
  LeaveRequestListResponse,
  CreateLeaveRequestInput,
  UpdateLeaveRequestInput,
  CalculateLeaveDaysInput,
  LeaveDaysCalculation,
  LeaveQueryFilters,
  ApproveLeaveInput,
  RejectLeaveInput,
  CreateLeaveTypeInput,
  UpdateLeaveTypeInput,
  CancelLeaveInput,
  WithdrawLeaveInput,
  PresignLeaveAttachmentInput,
  PresignedUploadResponse,
  ApprovalChainEntry,
  ApprovalStatus,
  LeaveTransaction,
  LeaveTransactionListResponse,
  LeaveTransactionFilters,
  LeaveEncashment,
  EncashmentListResponse,
  CreateEncashmentInput,
  BalanceAdjustmentInput,
  LeaveAdminOperationPreview,
  LeaveAdminOperationPreviewDetails,
  LeaveAdminOperationRun,
  LeaveAdminOperationType,
  DelegationListResponse,
  ApprovalDelegation,
  CreateDelegationInput,
  ManagedEmployee,
} from '../types';

const BASE_URL = '/hrms/leave';

// =============================================================================
// NORMALIZATION HELPERS
// =============================================================================

type RawLeaveRequest = Partial<LeaveRequest> & {
  requestId?: string;
  approvedBy?: string;
  approvedAt?: string;
  approverName?: string;
  delegatedFromName?: string;
};

type LeaveRequestCollectionResponse = {
  requests?: RawLeaveRequest[];
  data?: RawLeaveRequest[];
  total?: number;
};

type RawLeaveTransaction = Omit<
  LeaveTransaction,
  'days' | 'balanceBefore' | 'balanceAfter' | 'leaveTypeName'
> & {
  days: number | string;
  balanceBefore?: number | string | null;
  balanceAfter?: number | string | null;
  leaveTypeName?: string;
  leaveType?: { name?: string };
};

type LeaveTransactionCollectionResponse = {
  data?: RawLeaveTransaction[];
  transactions?: RawLeaveTransaction[];
  total?: number;
};

type RawLeaveEncashment = Omit<LeaveEncashment, 'daysEncashed' | 'amount' | 'leaveTypeName'> & {
  daysEncashed: number | string;
  amount: number | string;
  leaveTypeName?: string;
  leaveType?: { name?: string };
};

type EncashmentCollectionResponse = {
  data?: RawLeaveEncashment[];
  encashments?: RawLeaveEncashment[];
  total?: number;
};

export type FetchLeaveTypesOptions = {
  includeInactive?: boolean;
};

/**
 * Normalize a leave request from backend field names to frontend field names.
 * Backend uses: requestId, approvedBy, approvedAt, approverName.
 * Frontend uses: id, approvedById, approvalDate, approvedByName.
 */
function normalizeRequest(r: RawLeaveRequest): LeaveRequest {
  return {
    ...r,
    id: r.id ?? r.requestId ?? '',
    employeeId: r.employeeId ?? '',
    leaveTypeId: r.leaveTypeId ?? '',
    startDate: r.startDate ?? '',
    endDate: r.endDate ?? '',
    totalDays: r.totalDays ?? 0,
    reason: r.reason ?? '',
    status: r.status ?? 'draft',
    createdAt: r.createdAt ?? '',
    updatedAt: r.updatedAt ?? '',
    approvedById: r.approvedById ?? r.approvedBy ?? undefined,
    approvalDate: r.approvalDate ?? r.approvedAt ?? undefined,
    approvedByName: r.approvedByName ?? r.approverName ?? undefined,
    approvedOnBehalfOf: r.approvedOnBehalfOf ?? r.delegatedFromName ?? undefined,
  };
}

// =============================================================================
// LEAVE TYPES
// =============================================================================

/**
 * Fetch all leave types
 * Normalizes backend field names (leaveTypeId → id)
 */
export async function fetchLeaveTypes(options: FetchLeaveTypesOptions = {}): Promise<LeaveType[]> {
  const types = await apiClient.get<(LeaveType & { leaveTypeId?: string })[]>(`${BASE_URL}/types`, {
    params: options.includeInactive ? { includeInactive: 'true' } : undefined,
  });
  return types.map((t) => ({
    ...t,
    id: t.id ?? t.leaveTypeId ?? '',
  }));
}

// =============================================================================
// LEAVE BALANCE
// =============================================================================

/**
 * Fetch leave balances for an employee
 * Normalizes backend field names (balanceId → id)
 */
export async function fetchLeaveBalance(
  employeeId: string,
  year?: number,
): Promise<LeaveBalance[]> {
  const params = year ? { year: year.toString() } : {};
  const balances = await apiClient.get<(LeaveBalance & { balanceId?: string })[]>(
    `${BASE_URL}/balance/${employeeId}`,
    { params },
  );
  return balances.map((b) => ({
    ...b,
    id: b.id ?? b.balanceId ?? '',
  }));
}

// =============================================================================
// LEAVE REQUESTS
// =============================================================================

/**
 * Fetch leave requests with optional filters
 * Backend returns { requests: [...], total: N }
 */
export async function fetchLeaveRequests(
  filters?: LeaveQueryFilters,
): Promise<LeaveRequestListResponse> {
  const params: Record<string, string> = {};

  if (filters?.employeeId) params.employeeId = filters.employeeId;
  if (filters?.leaveTypeId) params.leaveTypeId = filters.leaveTypeId;
  if (filters?.status) params.status = filters.status;
  if (filters?.fromDate) params.fromDate = filters.fromDate;
  if (filters?.toDate) params.toDate = filters.toDate;
  if (filters?.page) params.page = filters.page.toString();
  if (filters?.limit) params.limit = filters.limit.toString();
  if (filters?.teamView) params.teamView = filters.teamView;
  if (filters?.requesterId) params.requesterId = filters.requesterId;
  if (filters?.search) params.search = filters.search;
  if (filters?.departmentId) params.departmentId = filters.departmentId;

  const raw = await apiClient.get<LeaveRequestCollectionResponse>(`${BASE_URL}/requests`, {
    params,
  });

  // Backend returns { requests: [...], total } — normalize to frontend shape
  const items = raw.requests ?? raw.data ?? [];
  const requests = items.map(normalizeRequest);

  return {
    data: requests,
    total: raw.total ?? requests.length,
    page: filters?.page ?? 1,
    limit: filters?.limit ?? 20,
    hasMore: requests.length === (filters?.limit ?? 20),
  };
}

/**
 * Fetch a single leave request by ID
 */
export async function fetchLeaveRequest(id: string): Promise<LeaveRequest> {
  const raw = await apiClient.get<LeaveRequest>(`${BASE_URL}/requests/${id}`);
  return normalizeRequest(raw);
}

/**
 * Create a new leave request
 */
export async function createLeaveRequest(input: CreateLeaveRequestInput): Promise<LeaveRequest> {
  const raw = await apiClient.post<LeaveRequest>(`${BASE_URL}/requests`, input);
  return normalizeRequest(raw);
}

/**
 * Calculate leave days with server policy rules (weekends, holidays, sandwich rules).
 */
export async function calculateLeaveRequestDays(
  input: CalculateLeaveDaysInput,
): Promise<LeaveDaysCalculation> {
  return apiClient.post<LeaveDaysCalculation>(`${BASE_URL}/requests/calculate-days`, input);
}

/**
 * Update a draft leave request
 */
export async function updateLeaveRequest(
  id: string,
  input: UpdateLeaveRequestInput,
): Promise<LeaveRequest> {
  const raw = await apiClient.put<LeaveRequest>(`${BASE_URL}/requests/${id}`, input);
  return normalizeRequest(raw);
}

/**
 * Submit a draft leave request for approval
 */
export async function submitLeaveRequest(id: string): Promise<LeaveRequest> {
  const raw = await apiClient.put<LeaveRequest>(`${BASE_URL}/requests/${id}/submit`);
  return normalizeRequest(raw);
}

/**
 * Approve a leave request
 */
export async function approveLeaveRequest(
  id: string,
  input?: ApproveLeaveInput,
): Promise<LeaveRequest> {
  const raw = await apiClient.put<LeaveRequest>(`${BASE_URL}/requests/${id}/approve`, input ?? {});
  return normalizeRequest(raw);
}

/**
 * Reject a leave request
 */
export async function rejectLeaveRequest(
  id: string,
  input: RejectLeaveInput,
): Promise<LeaveRequest> {
  const raw = await apiClient.put<LeaveRequest>(`${BASE_URL}/requests/${id}/reject`, input);
  return normalizeRequest(raw);
}

/**
 * Cancel a leave request (with required reason)
 */
export async function cancelLeaveRequest(
  id: string,
  input: CancelLeaveInput,
): Promise<LeaveRequest> {
  const raw = await apiClient.put<LeaveRequest>(`${BASE_URL}/requests/${id}/cancel`, input);
  return normalizeRequest(raw);
}

/**
 * Withdraw a submitted leave request
 */
export async function withdrawLeaveRequest(
  id: string,
  input?: WithdrawLeaveInput,
): Promise<LeaveRequest> {
  const raw = await apiClient.put<LeaveRequest>(`${BASE_URL}/requests/${id}/withdraw`, input ?? {});
  return normalizeRequest(raw);
}

// =============================================================================
// LEAVE TYPE MANAGEMENT
// =============================================================================

/**
 * Create a new leave type
 */
export async function createLeaveType(data: CreateLeaveTypeInput): Promise<LeaveType> {
  return apiClient.post<LeaveType>(`${BASE_URL}/types`, data);
}

/**
 * Update a leave type
 */
export async function updateLeaveType(id: string, data: UpdateLeaveTypeInput): Promise<LeaveType> {
  return apiClient.put<LeaveType>(`${BASE_URL}/types/${id}`, data);
}

/**
 * Remove an unused leave type, or deactivate it when audit history exists.
 */
export async function deleteLeaveType(id: string): Promise<void> {
  await apiClient.delete(`${BASE_URL}/types/${id}`);
}

// =============================================================================
// LEAVE REQUEST ATTACHMENTS
// =============================================================================

/**
 * Upload a file attachment to a leave request (presign → PUT to S3)
 */
export async function uploadLeaveAttachment(
  requestId: string,
  file: File,
): Promise<LeaveAttachment> {
  // Step 1: Get presigned upload URL
  const input: PresignLeaveAttachmentInput = {
    fileName: file.name,
    contentType: file.type,
    sizeBytes: file.size,
  };
  const presigned = await apiClient.post<PresignedUploadResponse>(
    `${BASE_URL}/requests/${requestId}/attachments/presign`,
    input,
  );

  // Step 2: Upload file directly to S3 using presigned URL
  await fetch(presigned.upload.url, {
    method: 'PUT',
    headers: presigned.upload.headers ?? { 'Content-Type': file.type },
    body: file,
  });

  // Step 3: Return the attachment record (already created during presign)
  return {
    attachmentId: presigned.document.id,
    leaveRequestId: requestId,
    fileName: file.name,
    fileKey: presigned.document.fileKey,
    mimeType: file.type,
    fileSize: file.size,
    uploadedBy: null,
    uploadedAt: new Date().toISOString(),
  };
}

/**
 * Fetch all attachments for a leave request
 */
export async function fetchLeaveAttachments(requestId: string): Promise<LeaveAttachment[]> {
  return apiClient.get<LeaveAttachment[]>(`${BASE_URL}/requests/${requestId}/attachments`);
}

/**
 * Get a presigned download URL for an attachment
 */
export async function getLeaveAttachmentDownloadUrl(
  requestId: string,
  attachmentId: string,
): Promise<{ url: string; expiresAt: string }> {
  return apiClient.get<{ url: string; expiresAt: string }>(
    `${BASE_URL}/requests/${requestId}/attachments/${attachmentId}/download`,
  );
}

/**
 * Delete an attachment from a leave request
 */
export async function deleteLeaveAttachment(
  requestId: string,
  attachmentId: string,
): Promise<void> {
  await apiClient.delete(`${BASE_URL}/requests/${requestId}/attachments/${attachmentId}`);
}

// =============================================================================
// APPROVAL CHAIN
// =============================================================================

/**
 * Fetch approval chain for an employee
 */
export async function fetchApprovalChain(employeeId: string): Promise<ApprovalChainEntry[]> {
  return apiClient.get<ApprovalChainEntry[]>(`/hrms/hierarchy/approval-chain/${employeeId}`);
}

// =============================================================================
// APPROVAL STATUS
// =============================================================================

/**
 * Fetch multi-level approval status for a leave request
 */
export async function fetchApprovalStatus(requestId: string): Promise<ApprovalStatus> {
  return apiClient.get<ApprovalStatus>(`${BASE_URL}/requests/${requestId}/approval-status`);
}

// =============================================================================
// LEAVE TRANSACTIONS
// =============================================================================

/**
 * Fetch leave transactions (audit trail)
 */
export async function fetchLeaveTransactions(
  filters?: LeaveTransactionFilters,
): Promise<LeaveTransactionListResponse> {
  const params: Record<string, string> = {};
  if (filters?.employeeId) params.employeeId = filters.employeeId;
  if (filters?.leaveTypeId) params.leaveTypeId = filters.leaveTypeId;
  if (filters?.transactionType) params.transactionType = filters.transactionType;
  if (filters?.fromDate) params.fromDate = filters.fromDate;
  if (filters?.toDate) params.toDate = filters.toDate;
  if (filters?.page) params.page = filters.page.toString();
  if (filters?.limit) params.limit = filters.limit.toString();

  const raw = await apiClient.get<LeaveTransactionCollectionResponse>(`${BASE_URL}/transactions`, {
    params,
  });
  const items = raw.data ?? raw.transactions ?? [];

  // Normalize: backend maps leaveType.name → leaveTypeName; also handle Decimal
  const data: LeaveTransaction[] = items.map((txn) => ({
    ...txn,
    leaveTypeName: txn.leaveTypeName ?? txn.leaveType?.name ?? undefined,
    days: Number(txn.days ?? 0),
    balanceBefore: txn.balanceBefore != null ? Number(txn.balanceBefore) : undefined,
    balanceAfter: txn.balanceAfter != null ? Number(txn.balanceAfter) : undefined,
  }));

  return {
    data,
    total: raw.total ?? data.length,
    page: filters?.page ?? 1,
    limit: filters?.limit ?? 20,
  };
}

// =============================================================================
// LEAVE ENCASHMENT
// =============================================================================

/**
 * Request leave encashment
 */
export async function createEncashment(input: CreateEncashmentInput): Promise<LeaveEncashment> {
  return apiClient.post<LeaveEncashment>(`${BASE_URL}/encashments`, input);
}

/**
 * Fetch encashment requests
 */
export async function fetchEncashments(params?: {
  status?: string;
  page?: number;
  limit?: number;
}): Promise<EncashmentListResponse> {
  const queryParams: Record<string, string> = {};
  if (params?.status) queryParams.status = params.status;
  if (params?.page) queryParams.page = params.page.toString();
  if (params?.limit) queryParams.limit = params.limit.toString();

  const raw = await apiClient.get<EncashmentCollectionResponse>(`${BASE_URL}/encashments`, {
    params: queryParams,
  });
  const items = raw.data ?? raw.encashments ?? [];

  // Normalize: backend may return leaveType.name alongside leaveTypeName
  const data: LeaveEncashment[] = items.map((enc) => ({
    ...enc,
    leaveTypeName: enc.leaveTypeName ?? enc.leaveType?.name ?? undefined,
    // Prisma Decimal → number
    daysEncashed: Number(enc.daysEncashed ?? 0),
    amount: Number(enc.amount ?? 0),
  }));

  return {
    data,
    total: raw.total ?? data.length,
    page: params?.page ?? 1,
    limit: params?.limit ?? 20,
  };
}

/**
 * Approve an encashment request
 */
export async function approveEncashment(id: string): Promise<LeaveEncashment> {
  return apiClient.put<LeaveEncashment>(`${BASE_URL}/encashments/${id}/approve`);
}

/**
 * Reject an encashment request
 */
export async function rejectEncashment(id: string, reason: string): Promise<LeaveEncashment> {
  return apiClient.put<LeaveEncashment>(`${BASE_URL}/encashments/${id}/reject`, {
    rejectionReason: reason,
  });
}

// =============================================================================
// LEAVE TYPE INITIALIZATION (SAP SCC4 — Tenant Provisioning)
// =============================================================================

/**
 * Initialize default leave types for the tenant (idempotent).
 * Creates 4 India statutory leave types (CL, SL, EL, LWP) if they don't exist.
 */
export async function initializeLeaveTypes(): Promise<{
  message: string;
  created: number;
  skipped: number;
  leaveTypes: string[];
}> {
  return apiClient.post(`${BASE_URL}/types/initialize`);
}

// =============================================================================
// LEAVE ADMIN
// =============================================================================

/**
 * Initialize leave balances for all employees (admin backfill)
 */
export async function initializeAllBalances(
  year?: number,
  dryRun = false,
): Promise<{ message: string; year: number; operationRunId: string; dryRun: boolean }> {
  return apiClient.post<{ message: string; year: number; operationRunId: string; dryRun: boolean }>(
    `${BASE_URL}/admin/initialize-all-balances`,
    { year, dryRun },
  );
}

export async function fetchLeaveAdminOperationPreview(input: {
  operationType: LeaveAdminOperationType;
  targetYear?: number;
  targetMonth?: number;
}): Promise<LeaveAdminOperationPreview> {
  const params: Record<string, string> = { operationType: input.operationType };
  if (input.targetYear) params.targetYear = String(input.targetYear);
  if (input.targetMonth) params.targetMonth = String(input.targetMonth);
  return apiClient.get<LeaveAdminOperationPreview>(`${BASE_URL}/admin/operations/preview`, {
    params,
  });
}

export async function fetchLeaveAdminOperationPreviewDetails(input: {
  operationType: LeaveAdminOperationType;
  targetYear?: number;
  targetMonth?: number;
  limit?: number;
}): Promise<LeaveAdminOperationPreviewDetails> {
  const params: Record<string, string> = { operationType: input.operationType };
  if (input.targetYear) params.targetYear = String(input.targetYear);
  if (input.targetMonth) params.targetMonth = String(input.targetMonth);
  if (input.limit) params.limit = String(input.limit);
  return apiClient.get<LeaveAdminOperationPreviewDetails>(
    `${BASE_URL}/admin/operations/preview/details`,
    { params },
  );
}

export async function fetchLeaveAdminOperationRuns(limit = 10): Promise<LeaveAdminOperationRun[]> {
  return apiClient.get<LeaveAdminOperationRun[]>(`${BASE_URL}/admin/operation-runs`, {
    params: { limit: String(limit) },
  });
}

/**
 * Adjust an employee's leave balance (admin)
 */
export async function adjustLeaveBalance(input: BalanceAdjustmentInput): Promise<void> {
  await apiClient.post(`${BASE_URL}/admin/adjust-balance`, input);
}

// =============================================================================
// DELEGATIONS (SAP HRBAS00 SUBSTITUTION)
// =============================================================================

/**
 * Fetch all delegations (from and to the current user)
 */
export async function fetchDelegations(): Promise<DelegationListResponse> {
  return apiClient.get<DelegationListResponse>(`${BASE_URL}/delegations`);
}

/**
 * Create an approval delegation
 */
export async function createDelegation(input: CreateDelegationInput): Promise<ApprovalDelegation> {
  return apiClient.post<ApprovalDelegation>(`${BASE_URL}/delegations`, input);
}

/**
 * Cancel an active delegation
 */
export async function cancelDelegation(id: string): Promise<void> {
  await apiClient.delete(`${BASE_URL}/delegations/${id}`);
}

// =============================================================================
// MANAGED EMPLOYEES (SAP P_ORGIN SCOPE)
// =============================================================================

/**
 * Fetch employees the current user can manage (for on-behalf operations).
 * Uses the hierarchy resolver's scope-based resolution.
 */
export async function fetchManagedEmployees(): Promise<ManagedEmployee[]> {
  return apiClient.get<ManagedEmployee[]>('/hrms/hierarchy/managed-employees');
}

// =============================================================================
// LEAVE ADMIN — BULK OPERATIONS (SAP PT_BAL00)
// =============================================================================

export async function triggerMonthlyAccrual(
  year: number,
  month: number,
  dryRun = false,
): Promise<{ message: string; operationRunId: string; dryRun: boolean }> {
  return apiClient.post(`${BASE_URL}/admin/trigger-monthly-accrual`, { year, month, dryRun });
}

export async function triggerQuarterlyAccrual(
  year: number,
  month: number,
  dryRun = false,
): Promise<{ message: string; operationRunId: string; dryRun: boolean }> {
  return apiClient.post(`${BASE_URL}/admin/trigger-quarterly-accrual`, { year, month, dryRun });
}

export async function triggerYearEndRollover(
  year: number,
  dryRun = false,
): Promise<{ message: string; operationRunId: string; dryRun: boolean }> {
  return apiClient.post(`${BASE_URL}/admin/trigger-year-end-rollover`, { year, dryRun });
}

export async function triggerExpiryCheck(
  dryRun = false,
): Promise<{ message: string; operationRunId: string; dryRun: boolean }> {
  return apiClient.post(`${BASE_URL}/admin/trigger-expiry-check`, { dryRun });
}

// =============================================================================
// CLIENT-SIDE CSV EXPORT HELPERS
// =============================================================================

/** Generate a CSV Blob from headers and rows */
export function toCsv(headers: string[], rows: string[][]): Blob {
  const csvContent = [
    headers.join(','),
    ...rows.map((row) =>
      row.map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','),
    ),
  ].join('\n');
  return new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
}

/** Trigger a browser file download from a Blob */
export function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// =============================================================================
// EXPORT
// =============================================================================

/**
 * Export leave requests as CSV.
 * Returns a Blob suitable for triggering a file download.
 */
export async function exportLeaveRequests(filters?: LeaveQueryFilters): Promise<Blob> {
  const params: Record<string, string> = { format: 'csv' };
  if (filters?.employeeId) params.employeeId = filters.employeeId;
  if (filters?.status) params.status = filters.status;
  if (filters?.fromDate) params.fromDate = filters.fromDate;
  if (filters?.toDate) params.toDate = filters.toDate;
  if (filters?.teamView) params.teamView = filters.teamView;
  if (filters?.requesterId) params.requesterId = filters.requesterId;

  // Use apiClient.get with responseType hint — axios passes config through
  return apiClient.get(`${BASE_URL}/requests/export`, {
    params,
    responseType: 'blob',
  });
}
